import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { translate } from '@jsverse/transloco';
import { InstancesService } from 'src/app/services/api/instances.service';
import { CustomToastrService } from 'src/app/services/util/toastr.service';

export interface DependencyNode {
  name: string;
  type?: string;
  version?: string;
  status?: string;
  isGroup?: boolean;
  children?: DependencyNode[];
  rawData?: any;
}

const GROUP_ORDER = ['tbc', 'tc', 'infra', 'exchange'];

@Component({
  selector: 'app-environment-dependency-tree',
  templateUrl: './environment-dependency-tree.component.html',
  styleUrl: './environment-dependency-tree.component.scss',
  standalone: false,
})
export class EnvironmentDependencyTreeComponent implements OnInit {
  @Input() instanceId: string;
  @Input() data: any;

  private destroyRef = inject(DestroyRef);
  private allNodes: DependencyNode[] = [];
  private expandedKeys = new Set<string>();

  treeData: DependencyNode[] = [];
  isLoading = false;
  hasError = false;
  searchTerm = '';
  activeTab = 0;
  selectedNode: DependencyNode | null = null;
  instructionType = '';

  constructor(
    private instancesService: InstancesService,
    private toastrService: CustomToastrService,
  ) {}

  ngOnInit(): void {
    this.loadDependencyTree();
  }

  selectNode(node: DependencyNode): void {
    this.selectedNode = node;
    this.activeTab = 1;
  }

  objectKeys(obj: any): string[] {
    return obj ? Object.keys(obj) : [];
  }

  loadDependencyTree(): void {
    this.isLoading = true;
    this.hasError = false;
    this.searchTerm = '';
    this.selectedNode = null;
    this.activeTab = 0;
    this.instructionType = '';
    this.instancesService.getInstanceDependencyTree(this.instanceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          if (response?.compositeInstruction?.instructionSteps?.length) {
            const types = [...new Set<string>(
              (response.compositeInstruction.instructionSteps as any[])
                .map((s: any) => s.type)
                .filter(Boolean)
            )];
            this.instructionType = types.join(' / ');
          }
          const nodes = this.buildTree(response);
          this.allNodes = nodes;
          this.expandedKeys.clear();
          this.treeData = [...nodes];
          this.isLoading = false;
        },
        error: (error: any) => {
          this.isLoading = false;
          this.hasError = true;
          const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message
            || error?.error?.message
            || error?.errmsg
            || translate('SERVICE.INTERNAL_SERVER_ERROR');
          this.toastrService.errMessage(message, title);
        },
      });
  }

  isExpanded(node: DependencyNode): boolean {
    return this.expandedKeys.has(this.nodeKey(node));
  }

  toggleNode(node: DependencyNode): void {
    const key = this.nodeKey(node);
    if (this.expandedKeys.has(key)) {
      this.expandedKeys.delete(key);
    } else {
      this.expandedKeys.add(key);
    }
  }

  getStatusClass(status: string): string {
    if (!status) return '';
    const s = status.toLowerCase();
    if (['deployed', 'running', 'active', 'success', 'initialized'].includes(s)) return 'status-success';
    if (['failed', 'error', 'update_failed'].includes(s)) return 'status-error';
    if (['pending', 'deploying', 'initializing', 'updating'].includes(s)) return 'status-pending';
    if (['stopped', 'terminated'].includes(s)) return 'status-stopped';
    return 'status-unknown';
  }

  onSearch(term: string): void {
    this.searchTerm = term ?? '';
    if (!term?.trim()) {
      this.treeData = [...this.allNodes];
      this.expandAll(this.allNodes);
      return;
    }
    const filtered = this.filterNodes(this.allNodes, term.toLowerCase());
    this.expandAll(filtered);
    this.treeData = [...filtered];
  }

  getGroupIcon(name: string): string {
    if (name === 'TBC') return 'layers';
    if (name === 'TC') return 'sensors';
    if (name === 'Infra') return 'dns';
    if (name === 'Exchange') return 'import_export';
    return 'description';
  }

  private nodeKey(node: DependencyNode): string {
    return `${node.name}|${node.type ?? ''}|${node.version ?? ''}`;
  }

  private expandAll(nodes: DependencyNode[]): void {
    for (const node of nodes) {
      if (node.children?.length) {
        this.expandedKeys.add(this.nodeKey(node));
        this.expandAll(node.children);
      }
    }
  }

  /**
   * Normalises any API response shape into TBC → TC → Infra grouped tree.
   *
   * Supported shapes:
   *  0. compositeInstruction: { compositeInstruction: { instructionSteps: [{ components: [...] }] } }
   *  1. Grouped object:  { tbc: [...], tc: [...], infra: [...] }
   *  2. Flat array with type field: [{ name, type: 'tbc', ... }]
   *  3. Already nested tree: [{ name, children: [...] }]
   *  4. Single root object: { name, children/dependencies: [...] }
   */
  private buildTree(response: any): DependencyNode[] {
    if (!response) return [];

    // Shape 0 — compositeInstruction envelope
    if (response.compositeInstruction) {
      return this.buildFromCompositeInstruction(response.compositeInstruction);
    }

    // Shape 1 — grouped object { tbc: [...], tc: [...], infra: [...] }
    const isGroupedObject =
      !Array.isArray(response) &&
      typeof response === 'object' &&
      GROUP_ORDER.some(key => Array.isArray(response[key]) || Array.isArray(response[key.toUpperCase()]));

    if (isGroupedObject) {
      return this.buildGroupedTree(response);
    }

    const items: any[] = Array.isArray(response) ? response : [response];

    // Shape 2 — flat array with type/componentType matching tbc/tc/infra
    const hasTypedItems = items.some(i =>
      GROUP_ORDER.includes((i.type || i.componentType || '').toLowerCase())
    );

    if (hasTypedItems) {
      const grouped: Record<string, any[]> = { tbc: [], tc: [], infra: [], exchange: [], other: [] };
      for (const item of items) {
        const t = (item.type || item.componentType || '').toLowerCase();
        (grouped[t] ?? grouped.other).push(item);
      }
      const nodes: DependencyNode[] = [];
      if (grouped.tbc.length) nodes.push({ name: 'TBC', type: 'group', isGroup: true, children: grouped.tbc.map(i => this.toNode(i)) });
      if (grouped.tc.length) nodes.push({ name: 'TC', type: 'group', isGroup: true, children: grouped.tc.map(i => this.toNode(i)) });
      if (grouped.infra.length) nodes.push({ name: 'Infra', type: 'group', isGroup: true, children: grouped.infra.map(i => this.toNode(i)) });
      if (grouped.exchange.length) nodes.push({ name: 'Exchange', type: 'group', isGroup: true, children: grouped.exchange.map(i => this.toNode(i)) });
      nodes.push(...grouped.other.map(i => this.toNode(i)));
      return nodes;
    }

    // Shape 3 — already nested, or no type grouping needed
    return items.map(i => this.toNode(i));
  }

  private buildFromCompositeInstruction(ci: any): DependencyNode[] {
    const seen = new Set<string>();
    const allComponents: any[] = [];
    for (const step of (ci.instructionSteps || [])) {
      for (const comp of (step.components || [])) {
        const key = `${(comp.componentId?.name || comp.capability || comp.componentDeployedName || '')}|${comp.componentType || ''}`;
        if (!seen.has(key)) {
          seen.add(key);
          allComponents.push(comp);
        }
      }
    }

    const grouped: Record<string, any[]> = { tbc: [], tc: [], infra: [], exchange: [], other: [] };
    for (const comp of allComponents) {
      const t = (comp.componentType || '').toLowerCase();
      (grouped[t] ?? grouped.other).push(comp);
    }

    const nodes: DependencyNode[] = [];
    if (grouped.tbc.length) nodes.push({ name: 'TBC', type: 'group', isGroup: true, children: grouped.tbc.map(c => this.toNode(c)) });
    if (grouped.tc.length) nodes.push({ name: 'TC', type: 'group', isGroup: true, children: grouped.tc.map(c => this.toNode(c)) });
    if (grouped.infra.length) nodes.push({ name: 'Infra', type: 'group', isGroup: true, children: grouped.infra.map(c => this.toNode(c)) });
    if (grouped.exchange.length) nodes.push({ name: 'Exchange', type: 'group', isGroup: true, children: grouped.exchange.map(c => this.toNode(c)) });
    nodes.push(...grouped.other.map(c => this.toNode(c)));
    return nodes;
  }

  private buildGroupedTree(response: any): DependencyNode[] {
    const nodes: DependencyNode[] = [];
    const tbc = this.resolveGroup(response, 'tbc');
    const tc = this.resolveGroup(response, 'tc');
    const infra = this.resolveGroup(response, 'infra');
    const exchange = this.resolveGroup(response, 'exchange');

    if (tbc.length) nodes.push({ name: 'TBC', type: 'group', isGroup: true, children: tbc });
    if (tc.length) nodes.push({ name: 'TC', type: 'group', isGroup: true, children: tc });
    if (infra.length) nodes.push({ name: 'Infra', type: 'group', isGroup: true, children: infra });
    if (exchange.length) nodes.push({ name: 'Exchange', type: 'group', isGroup: true, children: exchange });

    for (const key of Object.keys(response)) {
      if (!GROUP_ORDER.includes(key.toLowerCase()) && Array.isArray(response[key]) && response[key].length) {
        nodes.push({ name: key.toUpperCase(), type: 'group', isGroup: true, children: (response[key] as any[]).map(i => this.toNode(i)) });
      }
    }
    return nodes;
  }

  private resolveGroup(response: any, key: string): DependencyNode[] {
    const raw: any[] = response[key] || response[key.toUpperCase()] || [];
    return Array.isArray(raw) ? raw.map(i => this.toNode(i)) : [];
  }

  private toNode(item: any): DependencyNode {
    const children: any[] =
      item.children ||
      item.dependencies ||
      item.tcDependencies ||
      item.tbcDependencies ||
      item.infraDependencies ||
      item.subComponents ||
      [];

    return {
      name: item.componentId?.name || item.name || item.component || item.componentName
        || item.capability || item.componentDeployedName || item.displayName || item.id || 'Unknown',
      type: item.componentType || item.type,
      version: item.componentId?.version || item.releaseVersion || item.version || item.componentVersion || item.buildVersion,
      status: item.state || item.status || item.deploymentStatus,
      rawData: item,
      children: Array.isArray(children) && children.length > 0
        ? children.map(c => this.toNode(c))
        : undefined,
    };
  }

  private filterNodes(nodes: DependencyNode[], term: string): DependencyNode[] {
    return nodes.reduce<DependencyNode[]>((acc, node) => {
      const matchesSelf =
        node.name?.toLowerCase().includes(term) ||
        node.type?.toLowerCase().includes(term) ||
        node.version?.toLowerCase().includes(term) ||
        node.status?.toLowerCase().includes(term);
      const filteredChildren = node.children ? this.filterNodes(node.children, term) : [];
      if (matchesSelf || filteredChildren.length > 0) {
        acc.push({ ...node, children: filteredChildren.length > 0 ? filteredChildren : node.children });
      }
      return acc;
    }, []);
  }
}
